import { ReactElement, useEffect, useMemo, useState } from 'react';
import {  useDispatch, useSelector } from 'react-redux';
import { 
  AbstractEventAnalyticsLabel, Calendar, FetchReturn, RootState, ThunkDispatchType
} from '../../../store';
import { 
  fetchRemoteCalendars
} from '../../../store/schedule/actions';
import OrganizationEventTags from './OrganizationEventTags';
import {
  AutoAnalyticsEventCategoryLabels
} from './AutoAnalyticsEventCategoryLabels/AutoAnalyticsEventCategoryLabels';
import { Box, Typography, styled } from '@mui/material';
import colors from '../../../colors';
import { PermissionError } from '../../../utils/permissionUtils';
import {
  AnalyticsEventColor, AppAnalyticsAnalyticsColorsListApiResponse, AppAnalyticsEventCategoriesListApiResponse,
  AppAnalyticsEventTypesListApiResponse,
} from '../../../store/cabinetApi/generated/analytics';
import { analytics } from '../../../store/cabinetApi/analytics';


const StyledBox = styled(Box, {label: "StyledBox"})(() => ({
  width: '100%',
  marginTop: 8,
  border: '1px solid',
  borderColor: colors.black200,
  borderRadius: '4px',
}));

const OrganizationEventTagsContainer = ({ showLabels = true, showAutoAssign = true }: {
  showLabels?: boolean; showAutoAssign?: boolean
}): ReactElement => {
  const calendars = useSelector((root: RootState) => root.schedule.calendars);
  const user = useSelector((root: RootState) => root.auth.user);
  
  // RTK Query hooks
  const { data: eventCategoriesData } = analytics.useAppAnalyticsEventCategoriesListQuery();
  const { data: eventTypesData } = analytics.useAppAnalyticsEventTypesListQuery();
  const { data: analyticsColorsData } = analytics.useAppAnalyticsAnalyticsColorsListQuery({});
  
  // RTK Query mutations
  const [createEventCategory] = analytics.useAppAnalyticsEventCategoriesCreateMutation();
  const [updateEventCategory] = analytics.useAppAnalyticsEventCategoriesPartialUpdateMutation();
  const [deleteEventCategory] = analytics.useAppAnalyticsEventCategoriesDestroyMutation();
  
  const [createEventType] = analytics.useAppAnalyticsEventTypesCreateMutation();
  const [updateEventType] = analytics.useAppAnalyticsEventTypesPartialUpdateMutation();
  const [deleteEventType] = analytics.useAppAnalyticsEventTypesDestroyMutation();
  
  const [updateAnalyticsColor] = analytics.useAppAnalyticsAnalyticsColorsPartialUpdateMutation();

  const [saving, setSaving] = useState(false);
  const dispatch = useDispatch<ThunkDispatchType>();

  useEffect(() => {
    dispatch(fetchRemoteCalendars());
  }, [dispatch]);

  // Transform data from RTK Query format to expected format
  const eventCategories: AppAnalyticsEventCategoriesListApiResponse = eventCategoriesData || {};
  const eventTypes: AppAnalyticsEventTypesListApiResponse = eventTypesData || {};
  const analyticsColors: AppAnalyticsAnalyticsColorsListApiResponse = analyticsColorsData || [];

  const createLabel = async (
    action: "event_type" | "event_category",
    data: AbstractEventAnalyticsLabel,
    callback: () => void
  ): Promise<FetchReturn<AbstractEventAnalyticsLabel, PermissionError> | undefined> => {
    setSaving(true);
    let res: FetchReturn<AbstractEventAnalyticsLabel, PermissionError> | undefined = undefined;
    try {
      if (action === "event_category") {
        const result = await createEventCategory(data).unwrap();
        res = { status: 201, data: result };
        callback();
      } else if (action === "event_type") {
        const result = await createEventType(data).unwrap();
        res = { status: 201, data: result };
        callback();
      }
    } catch (error) {
      res = { 
        status: 400, 
        data: { 
          detail: error as PermissionError, 
          code: 'error' 
        } 
      };
    }
    setSaving(false);
    return res;
  };

  const updateLabel = async (
    action: "event_type" | "event_category",
    data: Partial<AbstractEventAnalyticsLabel> & Pick<AbstractEventAnalyticsLabel, 'id'>, callback: () => void
  ): Promise<FetchReturn<AbstractEventAnalyticsLabel, PermissionError> | undefined> => {
    setSaving(true);
    let res: FetchReturn<AbstractEventAnalyticsLabel, PermissionError> | undefined = undefined;
    try {
      if (action === "event_category") {
        const { id, ...rest } = data;
        const result = await updateEventCategory({ id: id, patchedEventCategory: rest }).unwrap();
        res = { status: 200, data: result };
        callback();
      } else if (action === "event_type") {
        const { id, ...rest } = data;
        const result = await updateEventType({ id: id, patchedEventType: rest }).unwrap();
        res = { status: 200, data: result };
        callback();
      }
    } catch (error) {
      res = { 
        status: 400, 
        data: { 
          detail: error as PermissionError, 
          code: 'error' 
        } 
      };
    }
    setSaving(false);
    return res;
  };

  const deleteLabel = (action: "event_type" | "event_category",
    id: number, callback: () => void) => {
    setSaving(true);
    if (action === "event_category") {
      deleteEventCategory(id).then(res => {
        if ('data' in res) {
          callback();
        }
        setSaving(false);
      }).catch(() => {
        setSaving(false);
      });
    } else if (action === "event_type") {
      deleteEventType(id).then(res => {
        if ('data' in res) {
          callback();
        }
        setSaving(false);
      }).catch(() => {
        setSaving(false);
      });
    }
  };

  const updateAnalyticsColorHandler = (color: AnalyticsEventColor & {id: number}) => {
    updateAnalyticsColor({ id: color.id, patchedAnalyticsEventColor: color });
  };

  const calendarsObject = useMemo(() => {
    const newCalendarsObject: {[key: string]: Calendar} = {};
    calendars.forEach(cal => {
      newCalendarsObject[cal.id] = cal;
    });
    return newCalendarsObject;
  }, [calendars]);
  
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 4
      }}>
      {showLabels && (
        <Box>
          <Typography variant="h2"
            sx={{
              marginBottom: 2,
              fontSize: "18px"
            }}
          >
            Label Management
          </Typography>
          <StyledBox>
            
            <OrganizationEventTags
              eventCategories={eventCategories}
              eventTypes={eventTypes}
              user={user}
              createLabel={createLabel}
              updateLabel={updateLabel}
              saving={saving}
              deleteLabel={deleteLabel}
            />
          </StyledBox>
        </Box>
      )}
      {showAutoAssign && (
        <Box>
          <Typography variant="h2"
            sx={{ 
              marginBottom: 0,
              fontSize: "18px"
            }}
          >
            Auto-Assign from Calendar Colors
          </Typography>
          <AutoAnalyticsEventCategoryLabels
            analyticsColors={analyticsColors}
            eventCategories={eventCategories}
            eventTypes={eventTypes}
            calendars={calendarsObject}
            updateAnalyticColor={updateAnalyticsColorHandler}
          />
        </Box>
      )}
    </Box>
  );
};

export default OrganizationEventTagsContainer;